import { LearnerPageChrome } from "@/pages/learners/page-chrome"
import { ActionLink } from "@/components/shared/action-buttons"
import {
  editLearnersSchoolStudent,
  editLearnersSchoolStudentPreferences,
  learnersSchoolStudent,
  learnersSchoolStudentPreferences,
  learnersStudentClasses,
  newLearnersSchoolStudent,
} from "@/routes"
import { useIndexProps } from "./page-props"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  BookOpen,
  CalendarCheck2,
  CalendarCog,
  Inbox,
  Pencil,
  PlusCircle,
  Trash,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Link, usePathParam } from "@/lib/page-rendering"

export default function Page() {
  const { students } = useIndexProps()
  const { t } = useTranslation(["school_students", "common"])
  const schoolId = usePathParam("schoolId")

  return (
    <>
      <LearnerPageChrome
        action={
          <ActionLink
            variant="solid"
            href={newLearnersSchoolStudent({ schoolId }).url}
            Icon={PlusCircle}
          >
            {t("index.add")}
          </ActionLink>
        }
      />
      <PageBody role="region">
        <Card>
          <CardContent>
            {students.length === 0 ? (
              <Empty data-testid="school_students-empty">
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t("school_students:index.empty_list_title")}
                  </EmptyTitle>
                  <EmptyDescription>
                    {t("school_students:index.empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Link
                    href={newLearnersSchoolStudent({ schoolId }).url}
                    as={Button}
                  >
                    <PlusCircle />
                    {t("school_students:index.add")}
                  </Link>
                </EmptyContent>
              </Empty>
            ) : (
              <ul className="grid w-full grid-cols-2 gap-4">
                {students.map((student) => (
                  <li key={student.student_id}>
                    <Card data-testid="student-card" className="h-full">
                      <CardHeader>
                        <CardTitle>
                          <Link
                            href={
                              learnersSchoolStudent({
                                schoolId,
                                id: student.student_id,
                              }).url
                            }
                          >
                            {student.first_name} {student.last_name}
                          </Link>
                        </CardTitle>
                        <CardDescription>{student.email}</CardDescription>
                      </CardHeader>
                      <CardFooter className="flex flex-row flex-wrap gap-4">
                        <Link
                          href={
                            editLearnersSchoolStudent({
                              schoolId,
                              id: student.student_id,
                            }).url
                          }
                          as={Button}
                        >
                          <Pencil />
                          {t("index.manage_student")}
                        </Link>
                        <Link
                          href={
                            learnersSchoolStudentPreferences({
                              schoolId,
                              studentId: student.student_id,
                            }).url
                          }
                          as={Button}
                        >
                          <CalendarCheck2 />
                          {t("index.view_student_preferences")}
                        </Link>
                        <Link
                          href={
                            editLearnersSchoolStudentPreferences({
                              schoolId,
                              studentId: student.student_id,
                            }).url
                          }
                          as={Button}
                        >
                          <CalendarCog />
                          {t("index.edit_preferences")}
                        </Link>

                        <Link
                          href={
                            learnersStudentClasses({
                              studentId: student.student_id,
                            }).url
                          }
                          as={Button}
                        >
                          <BookOpen />
                          {t("index.view_classes")}
                        </Link>

                        <ConfirmationDialog
                          action={
                            <Link
                              className={buttonVariants({
                                variant: "destructive",
                              })}
                              href={
                                learnersSchoolStudent({
                                  schoolId,
                                  id: student.student_id,
                                }).url
                              }
                              method="delete"
                              as={Button}
                              role="link"
                            >
                              {t("common:actions.confirm")}
                            </Link>
                          }
                        >
                          <ConfirmationDialogTrigger asChild>
                            <Button variant={"destructive-outline"}>
                              <Trash />
                              {t("common:actions.delete")}
                            </Button>
                          </ConfirmationDialogTrigger>
                        </ConfirmationDialog>
                      </CardFooter>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}
